package com.dotdrives.web.pages.settings

import com.dotdrives.controller.*
import com.dotdrives.model.*
import com.dotdrives.web.ui.*
import kotlinx.browser.window
import kotlinx.serialization.json.*
import react.*
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import web.cssom.ClassName

external interface ProviderWorkflowPanelProps : Props {
    var orgId: String
    var userId: String
    var role: String
    var provider: String
}

/** Provider URLs are validated independently of their transport envelope. */
fun allowedProviderUrl(raw: String): Boolean =
    Regex(
            "^https://(connect\\.stripe\\.com|checkout\\.stripe\\.com|billing\\.stripe\\.com|invoice\\.stripe\\.com|appcenter\\.intuit\\.com)/[^\\s]*$"
        )
        .matches(raw)

val ProviderWorkflowPanel =
    FC<ProviderWorkflowPanelProps> { props ->
        val (item, setItem) = useState("")
        val (account, setAccount) = useState("")
        val (refund, setRefund) = useState("disabled")
        val (gift, setGift) = useState("")
        val (remote, setRemote) = useState("")
        val (giver, setGiver) = useState("")
        val (customer, setCustomer) = useState("")
        val (mappingPreview, setMappingPreview) = useState<JsonObject?>(null)
        val (customerPreview, setCustomerPreview) = useState<JsonObject?>(null)
        val workflow = useWorkflowController(props.userId, props.orgId, props.role, props.provider)
        val s = useFlowState(workflow.state)
        useEffect(workflow) {
            workflow.read(props.provider)
            if (props.provider == "billing-portal") workflow.read(props.provider, "history")
        }
        useEffect(s.result) {
            s.result
                ?.takeIf { it.flag("eligible") && it.text("item_id") != null }
                ?.let { setMappingPreview(it) }
            s.result
                ?.takeIf { it.flag("eligible") && it.text("customer_id") != null }
                ?.let { setCustomerPreview(it) }
            s.result?.text("url")?.takeIf(::allowedProviderUrl)?.let { window.location.assign(it) }
        }
        if (!canAdminister(props.role))
            div {
                className = ClassName("settings-section-card")
                +"An owner or administrator manages provider connections and billing."
            }
        else {
            if (props.provider != "billing-portal") WorkflowFeedback {
                controller = workflow
                state = s
            }
            if ((props.provider == "billing-portal")) {
                // Keep transport diagnostics in controller state; present task-specific read failures.
                if (s.readErrors.isNotEmpty()) DotBanner {
                    message = "Billing details could not be loaded. Refresh billing to try again."
                }
                s.error?.let { error -> DotBanner {
                    message = if (error.contains("for invitations"))
                        "This billing action could not be completed. Retry the action or contact Dot Drives support."
                    else error
                    onRetry = if (s.pending != null) workflow::retry else null
                } }
                s.message?.let { div { role = "status".unsafeCast<react.dom.aria.AriaRole>(); +it } }
                if (s.pending != null && s.error == null) DotButton {
                    text = "Retry unfinished action"
                    variant = com.dotdrives.design.DotButtonVariant.Secondary
                    enabled = !s.busy
                    onClick = workflow::retry
                }
                BillingSubscriptionCard { this.workflow = workflow; state = s }
                div {
                    className = ClassName("settings-section-card")
                    div {
                        className = ClassName("settings-section-title")
                        +"Billing history"
                    }
                    val history = s.results["billing-portal:history"]?.get("history")?.jsonArray
                    if (history == null) div {
                        role = "status".unsafeCast<react.dom.aria.AriaRole>()
                        +if (s.readErrors.containsKey("billing-portal:history")) "Billing history could not be loaded. Refresh billing to try again." else "Loading billing history…"
                    }
                    if (history != null && history.isEmpty())
                        div { +"No billing activity recorded." }
                    history?.forEach { e ->
                        val row = e.jsonObject
                        div {
                            +"${row.text("occurred_at").orEmpty()} · ${row.text("kind").orEmpty()} · ${row.text("status").orEmpty()}"
                            row.text("hosted_url")?.takeIf(::allowedProviderUrl)?.let { url ->
                                a {
                                    href = url
                                    target = "_blank".unsafeCast<web.window.WindowTarget>()
                                    rel = "noopener noreferrer"
                                    +"View invoice"
                                }
                            }
                        }
                    }
                }
            } else {
                val stripe = s.results["stripe-connect:status"]
                val qbo = s.results["qbo-connect:status"]
                if (props.provider == "stripe-connect")
                    div {
                        className = ClassName("settings-section-card")
                        div {
                            className = ClassName("settings-section-title")
                            +"Stripe payments"
                        }
                        div {
                            +(when {
                                stripe == null -> "Connection status unavailable"
                                stripe.flag("connected") -> "Payments enabled"
                                else -> "Payments not enabled"
                            })
                        }
                        DotButton {
                            text = "Refresh Stripe status"
                            onClick = { workflow.read("stripe-connect") }
                        }
                        if (stripe?.flag("configured") == false)
                            div {
                                +"Payment setup is incomplete. Ask your administrator to configure Stripe, then refresh."
                            }
                        if (stripe?.text("next_action") == "create")
                            DotButton {
                                text = "Create payment account"
                                enabled = !(s.busy)
                                onClick = { workflow.perform("stripe-connect", "create") }
                            }
                        if (stripe?.text("next_action") == "onboard")
                            DotButton {
                                text = "Continue onboarding"
                                enabled = !(s.busy)
                                onClick = { workflow.perform("stripe-connect", "onboard") }
                            }
                        if (stripe?.text("next_action") == "portal_setup")
                            DotButton {
                                text = "Enable donor payment management"
                                onClick = { workflow.perform("stripe-connect", "portal_setup") }
                            }
                    }
                if (props.provider == "qbo-connect")
                    div {
                        className = ClassName("settings-section-card")
                        div {
                            className = ClassName("settings-section-title")
                            +"QuickBooks Online"
                        }
                        div {
                            +(when {
                                qbo == null -> "Connection status unavailable"
                                qbo.flag("connected") -> "Connected"
                                else -> "Not connected"
                            })
                        }
                        DotButton {
                            text = "Refresh QuickBooks status"
                            onClick = { workflow.read("qbo-connect") }
                        }
                        if (qbo?.flag("configured") == false)
                            div {
                                +"QuickBooks setup is incomplete. Ask your administrator to configure the connection service, then refresh."
                            }
                        DotButton {
                            text = "Connect QuickBooks"
                            enabled = !(s.busy)
                            onClick = { workflow.perform("qbo-connect", "connect") }
                        }
                        if (qbo?.flag("connected") == true) {
                            DotButton {
                                text = "Disconnect"
                                enabled = !(s.busy)
                                onClick = { workflow.perform("qbo-connect", "disconnect") }
                            }
                            label {
                                +"Donation item ID"
                                input {
                                    value = item
                                    onChange = { setItem(it.currentTarget.value) }
                                }
                            }
                            label {
                                +"Deposit account ID"
                                input {
                                    value = account
                                    onChange = { setAccount(it.currentTarget.value) }
                                }
                            }
                            label {
                                +"Refund handling"
                                select {
                                    value = refund
                                    onChange = { setRefund(it.currentTarget.value) }
                                    listOf("disabled", "refund_receipt").forEach {
                                        option {
                                            value = it
                                            +it.replace('_', ' ')
                                        }
                                    }
                                }
                            }
                            DotButton {
                                text = "Validate mapping"
                                onClick = {
                                    workflow.perform(
                                        "qbo-connect",
                                        "mapping.preview",
                                        mapOf("item_id" to item, "deposit_account_id" to account),
                                    )
                                }
                            }
                            mappingPreview?.let { validated ->
                                div {
                                    +"Validated: ${validated.text("item_name")} · ${validated.text("account_name")}"
                                }
                            }
                            val mappingConfirmed =
                                mappingPreview?.text("item_id") == item &&
                                    mappingPreview?.text("account_id") == account
                            DotButton {
                                text = "Save validated mapping"
                                enabled = mappingConfirmed && !s.busy
                                onClick = {
                                    workflow.perform(
                                        "qbo-connect",
                                        "mapping.save",
                                        mapOf(
                                            "item_id" to item,
                                            "deposit_account_id" to account,
                                            "refund_policy" to refund,
                                        ),
                                    )
                                }
                            }
                            div {
                                +"Saved mapping: ${qbo.text("item_name") ?: "No item"} · ${qbo.text("deposit_account_name") ?: "No bank account"}"
                            }
                            div {
                                className = ClassName("settings-section-subtitle")
                                +"Refund receipts record negative gift adjustments in the original bank, item and customer accounts. This accounting entry does not issue a payment refund."
                            }
                            val donations =
                                qbo["donations"]?.jsonArray.orEmpty().map { it.jsonObject }
                            label {
                                +"Giver to map"
                                select {
                                    value = giver
                                    onChange = { setGiver(it.currentTarget.value) }
                                    option {
                                        value = ""
                                        +"Choose a giver"
                                    }
                                    donations
                                        .distinctBy { it.text("giver_id") }
                                        .forEach { row ->
                                            option {
                                                value = row.text("giver_id")
                                                +row.text("giver_name").orEmpty()
                                            }
                                        }
                                }
                            }
                            label {
                                +"Existing QuickBooks customer ID"
                                input {
                                    value = customer
                                    onChange = { setCustomer(it.currentTarget.value) }
                                }
                            }
                            DotButton {
                                text = "Validate customer"
                                enabled = customer.isNotBlank() && !s.busy
                                onClick = {
                                    workflow.perform(
                                        "qbo-connect",
                                        "customer.preview",
                                        mapOf("customer_id" to customer),
                                    )
                                }
                            }
                            customerPreview?.let { validated ->
                                div { +"Validated customer: ${validated.text("customer_name")}" }
                            }
                            DotButton {
                                text = "Save validated customer"
                                enabled =
                                    giver.isNotBlank() &&
                                        customerPreview?.text("customer_id") == customer &&
                                        !s.busy
                                onClick = {
                                    workflow.perform(
                                        "qbo-connect",
                                        "customer.set",
                                        mapOf("giver_id" to giver, "customer_id" to customer),
                                    )
                                }
                            }
                            label {
                                +"Gift to review"
                                select {
                                    value = gift
                                    onChange = { setGift(it.currentTarget.value) }
                                    option {
                                        value = ""
                                        +"Choose a gift"
                                    }
                                    donations.forEach { row ->
                                        option {
                                            value = row.text("id")
                                            +"${row.text("giver_name")} · ${row.text("status")?.replace('_',' ')} · ${row.text("id")?.takeLast(8)}"
                                        }
                                    }
                                }
                            }
                            val selected = donations.firstOrNull { it.text("id") == gift }
                            selected?.let { row ->
                                div {
                                    +"Customer: ${row.text("customer_name") ?: "Not mapped"} · ${row.text("status")?.replace('_',' ')}"
                                }
                            }
                            if (selected?.text("status") in setOf("not_queued", "failed"))
                                DotButton {
                                    text = "Queue safe retry"
                                    enabled = !s.busy
                                    onClick = {
                                        workflow.perform(
                                            "qbo-sync",
                                            "retry",
                                            mapOf("donation_id" to gift),
                                        )
                                    }
                                }
                            if (selected?.text("status") == "reconciliation_required") {
                                div {
                                    +"An earlier attempt needs review. Verify the existing transaction; if none matches, contact your accountant or support."
                                }
                                label {
                                    +"Existing QuickBooks receipt ID"
                                    input {
                                        value = remote
                                        onChange = { setRemote(it.currentTarget.value) }
                                    }
                                }
                                DotButton {
                                    text = "Verify existing receipt"
                                    enabled = remote.isNotBlank() && !s.busy
                                    onClick = {
                                        workflow.perform(
                                            "qbo-sync",
                                            "reconcile",
                                            mapOf("donation_id" to gift, "remote_id" to remote),
                                        )
                                    }
                                }
                            }
                        }
                    }
            }
        }
    }
